import * as React from "react"
import { createPortal } from "react-dom"

import { cn } from "@/utils"

export interface TooltipProps {
  /** Tooltip text content */
  title: React.ReactNode
  /** Placement of the tooltip */
  placement?: "top" | "bottom" | "left" | "right"
  /** Additional className for the tooltip popup */
  className?: string
  children: React.ReactElement
}

/**
 * Portal-based tooltip. Renders in document.body so never clipped
 * by overflow parents. Measures the first child element for positioning.
 */
function Tooltip({ title, placement = "top", className, children }: TooltipProps) {
  const [show, setShow] = React.useState(false)
  const [pos, setPos] = React.useState({ top: 0, left: 0 })
  const triggerRef = React.useRef<HTMLDivElement>(null)

  if (!title) return children

  const updatePosition = () => {
    // display:contents has no box — measure the first child element
    const child = triggerRef.current?.firstElementChild as HTMLElement | null
    if (!child) return
    const rect = child.getBoundingClientRect()
    let top = 0
    let left = 0
    switch (placement) {
      case "top":
        top = rect.top - 8
        left = rect.left + rect.width / 2
        break
      case "bottom":
        top = rect.bottom + 8
        left = rect.left + rect.width / 2
        break
      case "left":
        top = rect.top + rect.height / 2
        left = rect.left - 8
        break
      case "right":
        top = rect.top + rect.height / 2
        left = rect.right + 8
        break
    }
    setPos({ top, left })
  }

  const transformOrigin = {
    top: "translate(-50%, -100%)",
    bottom: "translate(-50%, 0)",
    left: "translate(-100%, -50%)",
    right: "translate(0, -50%)",
  }

  return (
    <>
      <div
        ref={triggerRef}
        className="contents"
        onMouseEnter={() => { updatePosition(); setShow(true) }}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show &&
        createPortal(
          <div
            role="tooltip"
            className={cn(
              "fixed z-[9999] px-2 py-1 text-xs rounded-md whitespace-nowrap pointer-events-none",
              "bg-foreground text-background shadow-lg",
              "animate-in fade-in-0 zoom-in-95 duration-150",
              className
            )}
            style={{
              top: pos.top,
              left: pos.left,
              transform: transformOrigin[placement],
            }}
          >
            {title}
          </div>,
          document.body
        )}
    </>
  )
}

export { Tooltip }
